import { Gpt } from '@pcln/ad-unit'
import { SearchFormCard } from '@pcln/horizon-search-form'
import { Box, Card, Container, Hide, theme } from 'pcln-design-system'
import { type Ref, useState } from 'react'
import styled from 'styled-components'

import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import HeroImageParallax from '@/components/Heros/HeroImageParallax'
import { HeroRedesignLayout } from '@/components/Heros/HeroRedesignLayout'
import SearchFormProvider from '@/context/SearchFormContext'
import useContentfulSearchFormHeadlines from '@/hooks/useContentfulSearchFormHeadlines'
import useExpressDealDialog from '@/hooks/useExpressDealDialog'
import { usePostAdTargetHeight } from '@/hooks/usePostAdTargetHeight'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import ConditionalRender, { Experiment, Original } from '../ConditionalRender'
import { ExpressDealDialogExperiment } from './ExpressDeal/ExpressDealDialogExperiment'
import HorizonHotelForm from './HorizonForm/HorizonHotelForm'
import HotelsContent from './HotelsContent'
import HotelsSearchFormHeader from './HotelsSearchFormHeader'
import Form from './SearchForm/Form'
import { handleSubmitForHotels } from './SearchForm/utils'

const BlockFlex = styled(Box)`
  position: relative;
  z-index: 1;
  ${theme.mediaQueries[3]} {
    display: flex;
    align-items: flex-start;
  }
`

const HideAdUnit = styled(Hide)`
  text-align: center;
  position: relative;
  margin: 16px;
  min-height: 100px;
  ${theme.mediaQueries[3]} {
    width: 300px;
    flex: none;
    margin: 0 16px;
  }
`

const HOTELS_COMPANION_AD = <Gpt render={() => <div id='div-gpt-ad-1510170073577-0' />} />
const HOTELS_COMPANION_AD_VARIANT = <Gpt render={() => <div id='div-gpt-ad-1790171145044-0' />} />

type HotelsHeroSearchFormParams = Readonly<{
  location: LOCATION_SEARCH_TYPE | undefined
  searchFormRef: Ref<HTMLDivElement>
  setLocation: (location: LOCATION_SEARCH_TYPE | undefined) => void
  subheadline: string
}>

function HotelsHeroSearchForm({
  location,
  searchFormRef,
  setLocation,
  subheadline,
}: HotelsHeroSearchFormParams) {
  return (
    <ComponentMetricsBoundary componentId='SearchFormHotels'>
      <ConditionalRender experimentCode='USB_SEARCH_HOTEL_LANDING_HORIZON'>
        <Original>
          <Card
            ref={searchFormRef}
            data-testid='hero-search-form'
            width={1}
            borderWidth={0}
            color='background.lightest'
            borderRadius='2xl'
            boxShadowSize='xl'
          >
            <Box p={3} width={1}>
              <HotelsSearchFormHeader location={location} subheadline={subheadline} />
              <Form onSubmit={handleSubmitForHotels} setLocation={setLocation} showMultiHotel />
            </Box>
          </Card>
        </Original>
        <Experiment>
          <SearchFormCard ref={searchFormRef} data-testid='hero-search-form' variant='panel' width='full'>
            <HotelsSearchFormHeader location={location} subheadline={subheadline} />
            <SearchFormProvider>
              <HorizonHotelForm
                onSubmit={handleSubmitForHotels}
                setLocation={setLocation}
                showHotelTypeButtons
                pageName='landing'
              />
            </SearchFormProvider>
          </SearchFormCard>
        </Experiment>
      </ConditionalRender>
    </ComponentMetricsBoundary>
  )
}

type HotelsHeroContentParams = Readonly<{
  location: LOCATION_SEARCH_TYPE | undefined
  setLocation: (location: LOCATION_SEARCH_TYPE | undefined) => void
  subheadline: string
}>

function HotelsHeroContent({ location, setLocation, subheadline }: HotelsHeroContentParams) {
  const { searchFormRef, adUnitRef } = usePostAdTargetHeight({ enabled: false })

  return (
    <Container>
      <BlockFlex mx={3} py={[3, null, null, null, 5]}>
        <Box width={1}>
          <HotelsHeroSearchForm
            location={location}
            searchFormRef={searchFormRef}
            setLocation={setLocation}
            subheadline={subheadline}
          />
        </Box>
        <HideAdUnit ref={adUnitRef} xs sm>
          {HOTELS_COMPANION_AD}
        </HideAdUnit>
      </BlockFlex>
    </Container>
  )
}

function HotelsHeroRedesign({ location, setLocation, subheadline }: HotelsHeroContentParams) {
  const { searchFormRef, adUnitRef } = usePostAdTargetHeight({ enabled: true })

  return (
    <HeroRedesignLayout
      adBreakpoint='lg'
      adMinHeightClass='min-h-[100px]'
      adUnit={HOTELS_COMPANION_AD_VARIANT}
      adUnitRef={adUnitRef}
    >
      <HotelsHeroSearchForm
        location={location}
        searchFormRef={searchFormRef}
        setLocation={setLocation}
        subheadline={subheadline}
      />
    </HeroRedesignLayout>
  )
}

function HotelsDesktop() {
  const { isOpen, closeDialog } = useExpressDealDialog()
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const searchForm = useContentfulSearchFormHeadlines('searchForm')
  const subheadline = searchForm?.subheadline ?? ''
  const heroContentProps = {
    location,
    setLocation,
    subheadline,
  }

  return (
    <Box>
      <ExpressDealDialogExperiment isOpen={isOpen} closeDialog={closeDialog} />
      <ConditionalRender experimentCode='CG_PRD_HERO_REDESIGN'>
        <Original>
          <HeroImageParallax
            isScrollable
            gptId='div-gpt-ad-1509978154282-0'
            slot='/3102/priceline.dart/hotel_homepage_hero_image'
          >
            <HotelsHeroContent {...heroContentProps} />
          </HeroImageParallax>
        </Original>
        <Experiment>
          <HotelsHeroRedesign {...heroContentProps} />
        </Experiment>
      </ConditionalRender>
      <Hide xs sm color='background.lightest'>
        <HotelsContent />
      </Hide>
    </Box>
  )
}

export default HotelsDesktop
